/**
 * Family color assignment utility.
 *
 * Provides a palette of visually distinct background colors with good contrast
 * for overlaid text. Colors are assigned to families by sorted order of their
 * IDs within a given set, ensuring distinctness within any single view.
 */

/**
 * 20 visually distinct pastel background colors for family identification.
 *
 * Every colour is light enough to pair with **black text** at ≥ 4.5 : 1
 * contrast (WCAG AA).  A theme-aware border on FamilyColorBadge keeps
 * chips distinguishable against both light and dark page backgrounds.
 */
const FAMILY_PALETTE = [
  '#90CAF9', // Soft Blue        – black 12.0 : 1
  '#A5D6A7', // Soft Green       – black 12.8 : 1
  '#EF9A9A', // Soft Rose        – black  9.8 : 1
  '#CE93D8', // Soft Purple      – black  8.8 : 1
  '#F5C16B', // Soft Amber       – black 12.7 : 1
  '#80CBC4', // Soft Teal        – black 11.3 : 1
  '#F48FB1', // Soft Pink        – black  9.4 : 1
  '#B39DDB', // Soft Lavender    – black  8.8 : 1
  '#4DD0E1', // Soft Cyan        – black 11.4 : 1
  '#FFAB91', // Soft Peach       – black 11.5 : 1
  '#9FA8DA', // Soft Indigo      – black  9.1 : 1
  '#AED581', // Soft Lime        – black 12.6 : 1
  '#BCAAA4', // Soft Taupe       – black  9.4 : 1
  '#B0BEC5', // Soft Slate       – black 11.0 : 1
  '#F8BBD0', // Soft Magenta     – black 13.0 : 1
  '#81D4FA', // Soft Sky         – black 12.7 : 1
  '#D1C4E9', // Soft Violet      – black 12.8 : 1
  '#FFB74D', // Soft Tangerine   – black 12.1 : 1
  '#FF8A65', // Soft Coral       – black  9.1 : 1
  '#CDDC39', // Soft Chartreuse  – black 13.9 : 1
] as const

/**
 * Calculate relative luminance of an sRGB hex color per WCAG 2.x.
 * @see https://www.w3.org/TR/WCAG20/#relativeluminancedef
 */
function relativeLuminance(hex: string): number {
  const r = parseInt(hex.slice(1, 3), 16) / 255
  const g = parseInt(hex.slice(3, 5), 16) / 255
  const b = parseInt(hex.slice(5, 7), 16) / 255

  const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4)

  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
}

/**
 * Return the best text color (white or black) for a given background,
 * maximising WCAG contrast ratio.
 */
export function getContrastText(bgHex: string): '#ffffff' | '#000000' {
  const lum = relativeLuminance(bgHex)
  // Contrast ratio with white: (1.05) / (lum + 0.05)
  // Contrast ratio with black: (lum + 0.05) / 0.05
  const contrastWhite = 1.05 / (lum + 0.05)
  const contrastBlack = (lum + 0.05) / 0.05
  return contrastWhite >= contrastBlack ? '#ffffff' : '#000000'
}

/**
 * Build a deterministic color map from a set of identifiers.
 *
 * Identifiers are sorted so the same set always produces the same
 * assignment. Each identifier gets a unique palette slot (wrapping for >20
 * identifiers, though that is unlikely in practice).
 *
 * Callers should pass `familyId ?? userId` so that users without a family
 * still receive a unique color keyed by their user ID.
 *
 * @returns A `Map` from identifier to `{ bg, text }` where `bg` is the
 *   background hex color and `text` is the recommended overlay text color.
 */
export function buildFamilyColorMap(
  ids: (string | null | undefined)[]
): Map<string, { bg: string; text: string }> {
  const unique = [...new Set(ids.filter((id): id is string => !!id))].sort()
  const map = new Map<string, { bg: string; text: string }>()
  for (let i = 0; i < unique.length; i++) {
    const bg = FAMILY_PALETTE[i % FAMILY_PALETTE.length]
    map.set(unique[i], { bg, text: getContrastText(bg) })
  }
  return map
}

/**
 * Get the color for a specific identifier from a color map.
 * Callers should pass `familyId ?? userId` so that users without a family
 * still receive a color.
 */
export function getFamilyColor(
  colorMap: Map<string, { bg: string; text: string }>,
  id: string | null | undefined
): { bg: string; text: string } | undefined {
  if (!id) return undefined
  return colorMap.get(id)
}
